// Phase SA-7 — Per-autopsy leg card list.
//
// Thin wrapper around a sorted array of LegCard. Sorts by
// leg_order ascending so cards always display in submission order.
// Empty-state message when no legs (handled by SA-8 history
// expansion + by the autopsy-complete branch later).

import {
  type SlipAutopsyLegRow,
} from "../../slips/slipAutopsyLegs.ts";
import { LegCard } from "./LegCard.tsx";

export interface LegCardListProps {
  legs: ReadonlyArray<SlipAutopsyLegRow>;
  pro?: boolean;
  /** Optionally render the first card expanded by default. Useful
   * for the autopsy-complete state where we want the user to see
   * the verdict immediately. */
  expandFirst?: boolean;
}

export function LegCardList({
  legs,
  pro = false,
  expandFirst = false,
}: LegCardListProps): JSX.Element {
  if (legs.length === 0) {
    return (
      <div
        data-testid="sa-leg-card-list-empty"
        style={{
          padding: "16px 18px",
          border: "1px dashed var(--sa-hairline)",
          borderRadius: 4,
          background: "var(--sa-bg-raised)",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          color: "var(--sa-fg-muted)",
        }}
      >
        No legs to display for this autopsy.
      </div>
    );
  }

  // Stable copy + sort by leg_order ascending. Defensive — the
  // SA-2 index already orders by (autopsy_id, leg_order), but we
  // can't rely on caller hygiene.
  const sorted = [...legs].sort((a, b) => a.leg_order - b.leg_order);

  return (
    <div
      className="sa-leg-card-list"
      data-testid="sa-leg-card-list"
    >
      {sorted.map((leg, i) => (
        <LegCard
          key={leg.leg_id}
          leg={leg}
          pro={pro}
          defaultExpanded={expandFirst && i === 0}
        />
      ))}
    </div>
  );
}
